/**
 * Per-route SEO helper.
 *
 * The marketing site is a Vite SPA — the only static HTML bots see is
 * index.html. Google does render JS for indexing, but it ranks based
 * on what arrives in the initial HTML, so we update the head as soon
 * as a route mounts. This isn't as strong as full prerender/SSG, but
 * it's the highest-ROI mitigation we can ship without a build pipeline
 * rewrite (the audit's recommended next step is `vite-react-ssg`).
 *
 * Always pass an absolute path (starts with `/`) for `path`. The hook
 * builds the canonical + og:url from window.location.origin so the
 * `.ai` vs `.co` host inconsistency the audit flagged is resolved
 * automatically — whatever host the user landed on becomes the
 * canonical for that visit.
 */
import { useEffect } from "react";

export interface SeoOptions {
  title: string;
  description: string;
  /** Absolute path, e.g. "/states/california". Used to build canonical + og:url. */
  path: string;
  /** Optional override for og:type. Defaults to "website". */
  ogType?: "website" | "article";
  /** Optional structured data (JSON-LD). Pass a single object or array; the
   * hook stringifies + injects a single <script type="application/ld+json">. */
  jsonLd?: object | object[];
  /** Optional `<meta name="robots">`. Default: omitted (page indexable). */
  robots?: "noindex" | "noindex, nofollow";
  /** Optional canonical override — useful for variant pages that should point
   * back to a parent. E.g. /states/california?utm=... → /states/california. */
  canonicalOverride?: string;
  /** Absolute image URL for og:image / twitter:image. Falls back to the
   * default brand OG card when omitted. */
  image?: string | null;
}

function upsertMeta(key: string, content: string): void {
  const isOg = key.startsWith("og:");
  const isTwitter = key.startsWith("twitter:");
  const selector = isOg
    ? `meta[property="${key}"]`
    : isTwitter
      ? `meta[name="${key}"]`
      : `meta[name="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    if (isOg) el.setAttribute("property", key);
    else el.setAttribute("name", key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(key: string): void {
  const isOg = key.startsWith("og:");
  const selector = isOg ? `meta[property="${key}"]` : `meta[name="${key}"]`;
  const el = document.head.querySelector(selector);
  if (el) el.remove();
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(payload: object | object[]): void {
  // Remove any previously-injected per-route block. The shell ships its own
  // SoftwareApplication block that we leave alone.
  const existing = document.head.querySelector<HTMLScriptElement>(
    'script[type="application/ld+json"][data-route="1"]',
  );
  if (existing) existing.remove();
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.dataset.route = "1";
  el.textContent = JSON.stringify(payload);
  document.head.appendChild(el);
}

function clearJsonLd(): void {
  const existing = document.head.querySelector(
    'script[type="application/ld+json"][data-route="1"]',
  );
  if (existing) existing.remove();
}

export function useSeo(opts: SeoOptions): void {
  const { title, description, path, ogType = "website", jsonLd, robots, canonicalOverride, image } = opts;

  useEffect(() => {
    const origin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "https://nestplot.com";
    const canonical = canonicalOverride ?? `${origin}${path}`;
    const ogImage = image || `${origin}/brand/nestplot-og-1200x630.png`;

    document.title = title;
    upsertMeta("description", description);
    upsertMeta("og:title", title);
    upsertMeta("og:description", description);
    upsertMeta("og:url", canonical);
    upsertMeta("og:type", ogType);
    upsertMeta("og:image", ogImage);
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", title);
    upsertMeta("twitter:description", description);
    upsertMeta("twitter:image", ogImage);
    upsertCanonical(canonical);

    if (robots) upsertMeta("robots", robots);
    else removeMeta("robots");

    if (jsonLd) upsertJsonLd(jsonLd);
    else clearJsonLd();

    // No reset on unmount — the next route's useSeo() will overwrite the
    // values. Resetting to a shell-default would briefly flicker the title.
  }, [title, description, path, ogType, jsonLd, robots, canonicalOverride, image]);
}

/** Build the JSON-LD `BreadcrumbList` object for a path of crumbs. */
export function breadcrumbList(
  crumbs: { name: string; path: string }[],
): object {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://nestplot.com";
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${origin}${c.path}`,
    })),
  };
}

/** Build a FAQPage JSON-LD block. */
export function faqJsonLd(qa: { q: string; a: string }[]): object {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: qa.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

/** Build an Article JSON-LD block. */
export function articleJsonLd(opts: {
  headline: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified?: string;
  authorName?: string;
  /** Absolute image URL. Google Article rich results REQUIRE at
   * least one image — helper falls back to the default OG social
   * card when the post itself hasn't set a cover. */
  image?: string | null;
}): object {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://nestplot.com";
  const image = opts.image || `${origin}/brand/nestplot-og-1200x630.png`;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: opts.headline,
    description: opts.description,
    url: `${origin}${opts.path}`,
    image,
    datePublished: opts.datePublished,
    dateModified: opts.dateModified ?? opts.datePublished,
    author: { "@type": "Organization", name: opts.authorName ?? "NestPlot" },
    publisher: {
      "@type": "Organization",
      name: "NestPlot",
      url: origin,
      logo: {
        "@type": "ImageObject",
        url: `${origin}/brand/nestplot-mark-512.png`,
      },
    },
  };
}
